import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Phone, MapPin, Clock, Send, Sparkles, ArrowRight, CheckCircle } from 'lucide-react';

const Contact = () => {
  const { t, currentLanguage } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    type: 'general'
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('common.required', 'This field is required');
    }

    if (!formData.email.trim()) {
      errors.email = t('common.required', 'This field is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('common.invalidEmail', 'Please enter a valid email address');
    }

    if (!formData.subject.trim()) {
      errors.subject = t('common.required', 'This field is required');
    }

    if (!formData.message.trim()) {
      errors.message = t('common.required', 'This field is required');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Create a nicely formatted email body
    // Get inquiry type label in French
    const inquiryTypeLabels = {
      general: 'Demande Générale',
      sales: 'Ventes',
      support: 'Support Technique',
      partnership: 'Partenariat'
    };

    const getFrenchInquiryType = (type) => inquiryTypeLabels[type] || type;

    const emailBody = `
NOUVELLE DEMANDE DE CONTACT

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 INFORMATIONS DE CONTACT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
👤 Nom : ${formData.name}
📧 Email : ${formData.email}
🏢 Resturant : ${formData.company || 'Non fourni'}
📞 Téléphone : ${formData.phone || 'Non fourni'}
🎯 Type de demande : ${getFrenchInquiryType(formData.type)}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
💬 DÉTAILS DU MESSAGE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📌 Sujet : ${formData.subject}

Message :
${formData.message}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📤 ENVOYÉ VIA TABLEFLOW RESTAURATION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🕐 Date et heure : ${new Date().toLocaleString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    })}
`.trim();

    // Create mailto link
    const subject = `Contact Form: ${formData.subject}`;
    const mailtoLink = `mailto:mytableflow@outlook.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;

    // Open the user's email client
    window.open(mailtoLink, '_blank');

    // Show success message
    setIsSubmitted(true);

    // Reset form after success
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: '',
        type: 'general'
      });
      setIsSubmitted(false);
    }, 5000);
  };

  // Translation helper
  const getTranslatedItem = (key, fallback) => {
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  const getInquiryTypes = () => {
    if (currentLanguage === 'fr') {
      return [
        {
          value: 'general',
          label: 'Demande Générale',
          description: 'Questions générales sur notre plateforme'
        },
        {
          value: 'sales',
          label: 'Ventes',
          description: 'Intéressé par les tarifs ou plans entreprise'
        },
        {
          value: 'support',
          label: 'Support Technique',
          description: 'Besoin d\'aide avec votre compte'
        },
        {
          value: 'partnership',
          label: 'Partenariat',
          description: 'Explorer les opportunités de partenariat'
        }
      ];
    }

    if (currentLanguage === 'nl') {
      return [
        {
          value: 'general',
          label: 'Algemene Vraag',
          description: 'Algemene vragen over ons platform'
        },
        {
          value: 'sales',
          label: 'Verkoop',
          description: 'Geïnteresseerd in prijzen of enterprise-abonnementen'
        },
        {
          value: 'support',
          label: 'Technische Ondersteuning',
          description: 'Hulp nodig met uw account'
        },
        {
          value: 'partnership',
          label: 'Partnerschap',
          description: 'Verken samenwerkingsmogelijkheden'
        }
      ];
    }

    if (currentLanguage === 'de-ch') {
      return [
        {
          value: 'general',
          label: 'Allgemeine Anfrage',
          description: 'Allgemeine Fragen zu unserer Plattform'
        },
        {
          value: 'sales',
          label: 'Verkauf',
          description: 'Interessiert an Preisen oder Enterprise-Plänen'
        },
        {
          value: 'support',
          label: 'Technischer Support',
          description: 'Hilfe benötigt mit Ihrem Konto'
        },
        {
          value: 'partnership',
          label: 'Partnerschaft',
          description: 'Partnerschaftsmöglichkeiten erkunden'
        }
      ];
    }

    // English fallback
    return [
      {
        value: 'general',
        label: 'General Inquiry',
        description: 'General questions about our platform'
      },
      {
        value: 'sales',
        label: 'Sales',
        description: 'Interested in pricing or enterprise plans'
      },
      {
        value: 'support',
        label: 'Technical Support',
        description: 'Need help with your account'
      },
      {
        value: 'partnership',
        label: 'Partnership',
        description: 'Explore partnership opportunities'
      }
    ];
  };

  const GinquiryTypes = getInquiryTypes();

  const getFaqs = () => {
    switch (currentLanguage) {
      case 'fr':
        return [
          {
            question: "À quelle rapidité répondez-vous aux demandes ?",
            answer: "Nous répondons généralement dans les 2 heures pendant les heures de bureau et dans les 24 heures maximum."
          },
          {
            question: "Puis-je planifier une démo du produit ?",
            answer: "Absolument ! Vous pouvez planifier une démo personnalisée avec notre équipe commerciale via ce formulaire."
          },
          {
            question: "Quelle est la disponibilité de votre support client ?",
            answer: "Nous offrons un support email 24/7 et un support chat en direct de 6h à 21h , 7 jours par semaine."
          }
        ];
      case 'nl':
        return [
          {
            question: "Hoe snel reageren jullie op vragen?",
            answer: "We reageren meestal binnen 2 uur tijdens kantooruren en maximaal binnen 24 uur."
          },
          {
            question: "Kan ik een productdemo inplannen?",
            answer: "Absoluut! U kunt een gepersonaliseerde demo inplannen met ons verkoopteam via dit formulier."
          },
          {
            question: "Wat is de beschikbaarheid van jullie klantenondersteuning?",
            answer: "We bieden 24/7 e-mailondersteuning en live chatondersteuning van 6:00 tot 21:00 , 7 dagen per week."
          }
        ];
      case 'de-ch':
        return [
          {
            question: "Wie schnell reagieren Sie auf Anfragen?",
            answer: "Wir antworten in der Regel innerhalb von 2 Stunden während der Geschäftszeiten und maximal innerhalb von 24 Stunden."
          },
          {
            question: "Kann ich eine Produkt-Demo vereinbaren?",
            answer: "Absolut! Sie können eine personalisierte Demo mit unserem Vertriebsteam über dieses Formular vereinbaren."
          },
          {
            question: "Wie ist die Verfügbarkeit Ihres Kundensupports?",
            answer: "Wir bieten 24/7 E-Mail-Support und Live-Chat-Support von 6:00 bis 21:00 Uhr , 7 Tage die Woche."
          }
        ];
      default: // English fallback
        return [
          {
            question: "How quickly do you respond to inquiries?",
            answer: "We typically respond within 2 hours during business hours and within 24 hours maximum."
          },
          {
            question: "Can I schedule a product demo?",
            answer: "Absolutely! You can schedule a personalized demo with our sales team through this form."
          },
          {
            question: "What's your customer support availability?",
            answer: "We offer 24/7 email support and live chat support from 6 AM to 9 PM , 7 days a week."
          }
        ];
    }
  };

  const faqs = getFaqs();

  // Data
  const contactMethods = [
    {
      icon: Mail,
      title: getTranslatedItem('contact.methods.email.title', 'Email Us'),
      description: getTranslatedItem('contact.methods.email.description', 'Send us an email anytime'),
      details: 'mytableflow@outlook.com',
      gradient: 'from-blue-500 to-cyan-500',
      action: 'mailto:mytableflow@outlook.com'
    },
    {
      icon: Phone,
      title: getTranslatedItem('contact.methods.phone.title', 'Call Us'),
      description: getTranslatedItem('contact.methods.phone.description', 'Mon-Fri from 9am to 6pm'),
      details: '+41 78 752 22 64',
      gradient: 'from-green-500 to-emerald-500',
      action: 'tel:+41787522264'
    }
  ];


  const getQuickLinks = () => {
    switch (currentLanguage) {
      case 'fr':
        return [
          {
            label: "Voir les Tarifs",
            path: "/pricing"
          }
        ];
      case 'nl':
        return [
          {
            label: "Prijzen Bekijken",
            path: "/pricing"
          }
        ];
      case 'de-ch':
        return [
          {
            label: "Preise Anzeigen",
            path: "/pricing"
          }
        ];
      default: // English fallback
        return [
          {
            label: "View Pricing",
            path: "/pricing"
          }
        ];
    }
  };

  const quickLinks = getQuickLinks();

  const officeHours = {
    title: getTranslatedItem('contact.officeHours.title', 'Office Hours'),
    weekdays: getTranslatedItem('contact.officeHours.weekdays', 'Monday - Friday'),
    weekdaysTime: getTranslatedItem('contact.officeHours.weekdaysTime', '9:00 AM - 6:00 PM '),
    saturday: getTranslatedItem('contact.officeHours.saturday', 'Saturday'),
    saturdayTime: getTranslatedItem('contact.officeHours.saturdayTime', '10:00 AM - 4:00 PM '),
    sunday: getTranslatedItem('contact.officeHours.sunday', 'Sunday'),
    sundayTime: getTranslatedItem('contact.officeHours.sundayTime', 'Closed'),
    emergency: getTranslatedItem('contact.officeHours.emergency', 'Emergency Support:'),
    emergencyDescription: getTranslatedItem('contact.officeHours.emergencyDescription', 'Available 24/7 for critical system issues')
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_200px,#f0f9ff,transparent)]"></div>
      </div>

      {/* Header Section */}
      <section className="relative py-20 lg:py-28">
        <div className="container relative">
          <div className={`text-center max-w-4xl mx-auto transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Sparkles className="h-4 w-4" />
              {getTranslatedItem('contact.header.badge', "We're Here to Help")}
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {getTranslatedItem('contact.header.title.part1', 'Get in')}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {getTranslatedItem('contact.header.title.part2', ' Touch')}
              </span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {getTranslatedItem('contact.header.subtitle', 'Have questions about our platform? Our team is here to help you transform your restaurant with modern technology solutions.')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-10">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {contactMethods.map((method, index) => {
              const Icon = method.icon;
              return (
                <a
                  key={method.title}
                  href={method.action}
                  className={`group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  aria-label={`Contact us via ${method.title}`}
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r ${method.gradient} text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{method.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{method.description}</p>
                  <p className="text-gray-900 font-medium">{method.details}</p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className={`bg-white rounded-3xl shadow-xl p-8 transition-all duration-700 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  {getTranslatedItem('contact.form.title', 'Send us a Message')}
                </h2>
                <p className="text-gray-600">
                  {getTranslatedItem('contact.form.subtitle', "Fill out the form below and we'll get back to you as soon as possible.")}
                </p>
              </div>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {getTranslatedItem('contact.form.success.title', 'Opening Email Client...')}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {getTranslatedItem('contact.form.success.message', "Your message has been prepared. Please send it from your email client to mytableflow@outlook.com")}
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {getTranslatedItem('contact.form.success.anotherMessage', 'Send another message')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  {/* Inquiry Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {getTranslatedItem('contact.form.inquiryType.label', 'What can we help you with?')}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {GinquiryTypes.map((type, index) => (
                        <button
                          key={type.value || index}
                          type="button"
                          onClick={() => setFormData({ ...formData, type: type.value })}
                          className={`p-3 rounded-xl text-left transition-all duration-300 border-2 ${formData.type === type.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}
                          aria-pressed={formData.type === type.value}
                        >
                          <div className={`font-medium ${formData.type === type.value ? 'text-blue-700' : 'text-gray-900'
                            }`}>
                            {type.label}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{type.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Name & Company */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="group">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        {getTranslatedItem('contact.form.name.label', 'Full Name *')}
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className={`block w-full px-4 py-3 bg-white border rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300 ${formErrors.name ? 'border-red-300' : 'border-gray-200'
                          }`}
                        placeholder={getTranslatedItem('contact.form.name.placeholder', 'Your full name')}
                        aria-invalid={!!formErrors.name}
                        aria-describedby={formErrors.name ? 'name-error' : undefined}
                      />
                      {formErrors.name && (
                        <p id="name-error" className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                      )}
                    </div>
                    <div className="group">
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        {getTranslatedItem('contact.form.company.label', 'Restaurant/Company')}
                      </label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        className="block w-full px-4 py-3 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
                        placeholder={getTranslatedItem('contact.form.company.placeholder', 'Your restaurant name')}
                      />
                    </div>
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="group">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        {getTranslatedItem('contact.form.email.label', 'Email Address *')}
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className={`block w-full px-4 py-3 bg-white border rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300 ${formErrors.email ? 'border-red-300' : 'border-gray-200'
                          }`}
                        placeholder={getTranslatedItem('contact.form.email.placeholder', 'your@email.com')}
                        aria-invalid={!!formErrors.email}
                        aria-describedby={formErrors.email ? 'email-error' : undefined}
                      />
                      {formErrors.email && (
                        <p id="email-error" className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                      )}
                    </div>
                    <div className="group">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        {getTranslatedItem('contact.form.phone.label', 'Phone Number')}
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="block w-full px-4 py-3 bg-white border border-gray-200 rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300"
                        placeholder={getTranslatedItem('contact.form.phone.placeholder', '+1 (555) 000-0000')}
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="group">
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      {getTranslatedItem('contact.form.subject.label', 'Subject *')}
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className={`block w-full px-4 py-3 bg-white border rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300 ${formErrors.subject ? 'border-red-300' : 'border-gray-200'
                        }`}
                      placeholder={getTranslatedItem('contact.form.subject.placeholder', 'What is this regarding?')}
                      aria-invalid={!!formErrors.subject}
                      aria-describedby={formErrors.subject ? 'subject-error' : undefined}
                    />
                    {formErrors.subject && (
                      <p id="subject-error" className="mt-1 text-sm text-red-600">{formErrors.subject}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div className="group">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {getTranslatedItem('contact.form.message.label', 'Message *')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      required
                      value={formData.message}
                      onChange={handleChange}
                      className={`block w-full px-4 py-3 bg-white border rounded-xl placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-gray-300 resize-none ${formErrors.message ? 'border-red-300' : 'border-gray-200'
                        }`}
                      placeholder={getTranslatedItem('contact.form.message.placeholder', 'Tell us how we can help you...')}
                      aria-invalid={!!formErrors.message}
                      aria-describedby={formErrors.message ? 'message-error' : undefined}
                    />
                    {formErrors.message && (
                      <p id="message-error" className="mt-1 text-sm text-red-600">{formErrors.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center"
                    aria-label={getTranslatedItem('contact.form.submit', 'Send message via email')}
                  >
                    {getTranslatedItem('contact.form.submit', 'Send Message via Email')}
                    <Send className="ml-2 h-5 w-5" />
                  </button>

                  <p className="text-sm text-gray-500 text-center mt-4">
                    {getTranslatedItem('contact.form.note', 'Your email client will open with a pre-filled message to mytableflow@outlook.com')}
                  </p>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className={`space-y-8 transition-all duration-700 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
              {/* Office Hours */}
              <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-3xl p-8 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="h-6 w-6" />
                  <h3 className="text-xl font-bold">{officeHours.title}</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span>{officeHours.weekdays}</span>
                    <span className="font-semibold">{officeHours.weekdaysTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{officeHours.saturday}</span>
                    <span className="font-semibold">{officeHours.saturdayTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{officeHours.sunday}</span>
                    <span className="font-semibold">{officeHours.sundayTime}</span>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  <p className="text-sm text-blue-100">
                    <strong>{officeHours.emergency}</strong>{' '}
                    {officeHours.emergencyDescription}
                  </p>
                </div>
              </div>

              {/* FAQ */}
              <div className="bg-white rounded-3xl shadow-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {getTranslatedItem('contact.faq.title', 'Common Questions')}
                </h3>
                <div className="space-y-6">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
                      <h4 className="font-semibold text-gray-900 mb-2">{faq.question}</h4>
                      <p className="text-gray-600 text-sm">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-gray-50 rounded-3xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {getTranslatedItem('contact.quickLinks.title', 'Quick Links')}
                </h3>
                <div className="space-y-3">
                  {quickLinks.map((link, index) => (
                    <Link
                      key={index}
                      to={link.path}
                      className="flex items-center justify-between p-3 bg-white rounded-xl hover:shadow-md transition-shadow duration-300"
                    >
                      <span className="text-gray-700">{link.label}</span>
                      <ArrowRight className="h-4 w-4 text-gray-400" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section >

      {/* CTA Section */}
      < section className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600" >
        <div className="container text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getTranslatedItem('contact.cta.title', 'Ready to Get Started?')}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {getTranslatedItem('contact.cta.subtitle', 'Join thousands of restaurants already using our platform to streamline their operations.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-white text-blue-600 px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                {getTranslatedItem('navigation.register', 'Get Started Free')}
              </Link>
            </div>
          </div>
        </div>
      </section >
    </div >
  );
};

export default Contact;